import React, { useMemo } from "react";
import { shape, array, string } from "prop-types";
import { useLocale } from "@insightfulscience/smart-react/i18";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { file, pageProps as pagePropsType } from "@insightfulscience/contentful/utils/propTypes";
import renderBlockByType from "../../utility/renderBlockByType";

const isNeedShowMenu = slug => ["academy"].includes(slug);

const StandardPage = ({ post, pageProps }) => {
	const [t] = useLocale("standard-pages");
	const { siteDomain, prefix, components } = pageProps;
	const {
		metaTitle = "",
		metaDescription = "",
		metaCanonical,
		slug,
		thumbnail,
		contentReference,
		noindexTag = false,
	} = post;

	const thumbnailUrl = thumbnail?.fields?.file?.url || "";
	const url = `${siteDomain}/${prefix ? `${prefix}/` : ""}${slug}`;
	const menu = useMemo(
		() =>
			isNeedShowMenu(slug)
				? [
						{
							id: "menu",
							title: t("Menu"),
							items: contentReference
								.filter(
									({ sys: { contentType } }) => contentType?.sys?.id === "blockFeaturedContent",
								)
								.map(({ fields: { name, id } }) => ({
									id,
									title: name,
								}))
								.sort((a, b) => a.title.localeCompare(b.title)),
						},
				  ]
				: [],
		[slug, contentReference],
	);

	return (
		<>
			<PageMeta title={metaTitle} description={metaDescription}>
				<PageMeta.OGP
					title={metaTitle || null}
					description={metaDescription || null}
					url={url || null}
					image={thumbnailUrl || t("META_IMAGE")}
					imageSecureUrl={thumbnailUrl || t("META_IMAGE_SECURE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Twitter
					card={t("META_TWITTER_CARD")}
					site={t("META_TWITTER_SITE")}
					creator={t("META_TWITTER_CREATOR")}
					title={metaTitle || null}
					description={metaDescription || null}
					image={thumbnailUrl || t("META_TWITTER_IMAGE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				{noindexTag ? <PageMeta.Robots content="noindex, follow" /> : null}
				<PageMeta.Canonical href={metaCanonical || url} />
			</PageMeta>

			{contentReference.map(renderBlockByType(slug, components, menu))}
		</>
	);
};

StandardPage.propTypes = {
	post: shape({
		metaTitle: string,
		metaDescription: string,
		metaCanonical: string,
		slug: string.isRequired,
		thumbnail: file,
		contentReference: array, // eslint-disable-line react/forbid-prop-types
	}),
	pageProps: pagePropsType,
};

export default StandardPage;
